import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const t = localStorage.getItem("mobile_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Iter 91.11 — Landing page after Stripe redirect for parent charges.
// Confirms payment status, shows breakdown (subtotal + card fee = total)
// and bounces the parent back to the app dashboard.
const MobilePaySuccessPage = () => {
  const [state, setState] = useState({ phase: "checking", message: "Ελεγχος πληρωμης..." });
  const [tx, setTx] = useState(null);
  const pollRef = useRef({ attempts: 0, timer: null });

  useEffect(() => {
    const pollState = pollRef.current;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState({ phase: "error", message: "Δεν βρεθηκε ID συνεδριας" });
      return;
    }

    const poll = async () => {
      pollState.attempts += 1;
      try {
        const res = await axios.get(`${API}/mobile/charges/stripe-status/${sessionId}`, { headers: getAuthHeaders() });
        const { payment_status, status } = res.data;
        setTx(res.data);
        if (payment_status === "paid") {
          setState({ phase: "success", message: "Η πληρωμη ολοκληρωθηκε!" });
          return;
        }
        if (status === "expired") {
          setState({ phase: "error", message: "Η συνεδρια πληρωμης εληξε." });
          return;
        }
        if (pollState.attempts >= 12) {
          setState({ phase: "error", message: "Η πληρωμη δεν επιβεβαιωθηκε. Δοκιμαστε ξανα σε λιγο." });
          return;
        }
        pollState.timer = setTimeout(poll, 2000);
      } catch (e) {
        setState({ phase: "error", message: e.response?.data?.detail || "Σφαλμα ελεγχου πληρωμης" });
      }
    };
    poll();
    return () => { if (pollState.timer) clearTimeout(pollState.timer); };
  }, []);

  const back = () => { window.location.href = "/app"; };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-6 text-white" data-testid="mobile-pay-success">
      <div className="max-w-sm w-full text-center bg-[#121212] border border-[#262626] rounded-2xl p-8">
        {state.phase === "checking" && (
          <>
            <Loader2 size={48} className="mx-auto text-[#F5A623] mb-4 animate-spin" />
            <h1 className="font-['Bebas_Neue'] text-xl mb-2">{state.message}</h1>
            <p className="text-zinc-400 text-xs">Παρακαλω μην κλεισετε την εφαρμογη.</p>
          </>
        )}
        {state.phase === "success" && (
          <>
            <CheckCircle size={56} className="mx-auto text-emerald-400 mb-4" />
            <h1 className="font-['Bebas_Neue'] text-xl mb-2">Επιτυχια!</h1>
            {tx && (
              <div className="text-left bg-[#0a0a0a] border border-white/[0.06] rounded-xl p-3 my-3 space-y-1.5">
                <div className="flex justify-between text-[11px] text-zinc-400"><span>Χρεωσεις</span><span className="text-white">€{Number(tx.subtotal || 0).toFixed(2)}</span></div>
                <div className="flex justify-between text-[11px] text-zinc-400"><span>Προμηθεια Καρτας</span><span className="text-[#F5A623]">€{Number(tx.card_fee || 0).toFixed(2)}</span></div>
                <div className="h-px bg-white/[0.06]" />
                <div className="flex justify-between text-xs"><span className="text-white font-semibold">Συνολο</span><span className="text-emerald-400 font-bold">€{Number(tx.amount_total ? tx.amount_total / 100 : (tx.subtotal || 0) + (tx.card_fee || 0)).toFixed(2)}</span></div>
              </div>
            )}
            <p className="text-zinc-500 text-xs mb-4">Οι χρεωσεις σας ενημερωθηκαν αυτοματα.</p>
            <button onClick={back} className="w-full bg-[#F5A623] text-black font-semibold text-sm py-3 rounded-lg" data-testid="mobile-pay-success-back">
              Επιστροφη στην εφαρμογη
            </button>
          </>
        )}
        {state.phase === "error" && (
          <>
            <XCircle size={56} className="mx-auto text-red-400 mb-4" />
            <h1 className="font-['Bebas_Neue'] text-xl mb-2">Σφαλμα</h1>
            <p className="text-zinc-400 text-xs mb-6">{state.message}</p>
            <button onClick={back} className="w-full border border-zinc-600 text-zinc-300 text-sm py-3 rounded-lg" data-testid="mobile-pay-success-back">
              Επιστροφη στην εφαρμογη
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default MobilePaySuccessPage;
